import { useRef, useState } from 'react';

const services = [
  {
    title: 'ERP Consulting Services',
    description:
      'A well-designed ERP system streamlines processes and automates operations. Our consulting services help businesses evaluate existing systems, identify bottlenecks, and create a data-driven roadmap for a robust solution tailored to their needs. We support process optimization, migration, end-to-end implementation, and ongoing ERP support.',
  },
  {
    title: 'CRM Consulting Services',
    description:
      'Personalized customer experiences help build trust and loyalty. A robust CRM system with real-time insights and process automation gives teams a clearer view of customer relationships. We help organizations plan and implement CRM solutions, including capabilities for sales, marketing, customer service, and field service.',
  },
  {
    title: 'Solution Design',
    description:
      'An ERP or CRM platform delivers the most value when it fits your business requirements. Our consultants assess your systems, uncover performance bottlenecks, and design a process-led solution around your goals, helping your teams get more value from their enterprise software.',
  },
  {
    title: 'CRM/ERP Integration',
    description:
      'Connect CRM and ERP platforms with your existing systems and third-party tools to improve data accuracy, unlock timely insights, and automate workflows. Connected systems help teams make informed decisions, streamline operations, and deliver more consistent customer experiences.',
  },
  {
    title: 'CRM/ERP Implementation Consulting',
    description:
      'Our consultants guide your implementation from planning through deployment. We help assess current systems, evaluate costs and benefits, plan resources, select software and deployment models, and map business processes—so the solution is aligned with the way your organization works.',
  },
  {
    title: 'CRM/ERP Migration Consulting',
    description:
      'When your current platform no longer meets business needs, a modern cloud-based solution can help improve agility and manage costs. Our migration consultants assess your processes and guide the transition to a solution that supports automation, efficient operations, and data-informed decisions.',
  },
];

export default function ServicesOverview() {
  const [activeIndex, setActiveIndex] = useState(0);
  const tabRefs = useRef([]);
  const activeService = services[activeIndex];

  const handleTabKeyDown = (event) => {
    let nextIndex = activeIndex;
    if (event.key === 'ArrowDown') nextIndex = (activeIndex + 1) % services.length;
    else if (event.key === 'ArrowUp') nextIndex = (activeIndex - 1 + services.length) % services.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = services.length - 1;
    else return;

    event.preventDefault();
    setActiveIndex(nextIndex);
    tabRefs.current[nextIndex]?.focus();
  };

  return (
    <section
      className="relative isolate overflow-hidden px-6 py-12 text-white sm:px-8 sm:py-14 lg:px-[7vw]"
      style={{
        backgroundColor: '#293167',
        backgroundImage:
          "url('https://dynatechconsultancy.com/hubfs/DynaTech_Systems_October2023/Images/5-1.webp')",
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }}
      aria-labelledby="enterprise-services-overview-title"
    >
      <div
        className="absolute inset-0 -z-10"
        style={{ backgroundColor: 'rgba(41, 49, 103, 0.88)' }}
        aria-hidden="true"
      />

      <div
        className="absolute inset-0 -z-10"
        style={{ backgroundColor: 'rgba(41, 49, 103, 0.88)' }}
        aria-hidden="true"
      />

      <div className="w-full">
        <h2
          id="enterprise-services-overview-title"
          className="mb-8 text-center sm:mb-10 text-[32px] font-semibold leading-[48px]"
          style={{ color: '#ffffff' }}
        >
          An Overview of CRM and ERP Consulting Services
        </h2>

        <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-[minmax(240px,0.32fr)_minmax(0,1fr)] md:gap-10">
          <div
            className="flex flex-col border-b border-white/30 pb-4 md:border-b-0 md:border-r md:border-white/30 md:pb-0 md:pr-6"
            role="tablist"
            aria-label="CRM and ERP consulting services"
            aria-orientation="vertical"
            onKeyDown={handleTabKeyDown}
          >
            {services.map((service, index) => (
              <button
                key={service.title}
                ref={(element) => { tabRefs.current[index] = element; }}
                id={`enterprise-service-tab-${index}`}
                type="button"
                role="tab"
                aria-selected={activeIndex === index}
                aria-controls="enterprise-service-panel"
                tabIndex={activeIndex === index ? 0 : -1}
                onClick={() => setActiveIndex(index)}
                className="w-full border-l-4 px-4 py-3 text-left text-sm font-medium leading-snug transition-colors sm:text-base"
                style={{
                  borderColor: activeIndex === index ? '#31b7ed' : 'transparent',
                  backgroundColor: activeIndex === index ? 'rgba(255, 255, 255, 0.16)' : 'transparent',
                  color: '#ffffff',
                }}
              >
                {service.title}
              </button>
            ))}
          </div>

          <div
            id="enterprise-service-panel"
            className="min-h-48 py-2 md:px-2 md:py-4"
            role="tabpanel"
            aria-labelledby={`enterprise-service-tab-${activeIndex}`}
            tabIndex={0}
          >
            <h3 className="mb-4 text-xl font-semibold sm:text-2xl" style={{ color: '#ffffff' }}>
              {activeService.title}
            </h3>
            <p className="text-base font-normal leading-[27px]" style={{ color: '#ffffff' }}>
              {activeService.description}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
